<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAcesso } from 'dashboard/composables/useAcesso';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// O interruptor "Contato de teste" (REPORTS-04 D28). Ligado, as conversas do contato, passadas e
// futuras, saem dos relatórios. Só o administrador o vê: o servidor recusa a mudança de quem não
// é (D25), e quem não é admin vê só a etiqueta "teste" nas conversas.
const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, marcar o contato de teste é do `contacts:manage`
// (`contacts#update`, `recusar_mudanca_de_teste`); desligada, do administrador, como hoje.
const { decide } = useAcesso();
const marcaTeste = computed(() =>
  decide('contacts:manage', () => isAdmin.value)
);

const gravando = ref(false);
// O motivo com que o servidor recusou a mudança, num 422: o contato do Playground não deixa de ser
// de teste (D26). Com ele, o interruptor fica travado e o motivo aparece no lugar da descrição.
const recusa = ref('');

watch(
  () => props.contact.id,
  () => {
    recusa.value = '';
  }
);

// O contato do Playground já vem travado no payload (D37), e o interruptor abre travado, com o
// motivo, sem esperar o 422 do primeiro clique. A recusa do servidor segue valendo para o contato
// que chegou ao store sem a trava. A trava chega em duas formas: o painel da conversa passa o
// payload como veio (teste_travado), e a página do contato passa o contato do getContactById,
// que o põe em camelCase (testeTravado).
const travado = computed(
  () => !!(props.contact.teste_travado ?? props.contact.testeTravado)
);
const motivo = computed(
  () =>
    recusa.value ||
    (travado.value ? t('CONTACT_PANEL.TEST_CONTACT.LOCKED') : '')
);

const alternar = async teste => {
  gravando.value = true;
  try {
    await store.dispatch('contacts/update', { id: props.contact.id, teste });
    useAlert(
      teste
        ? t('CONTACT_PANEL.TEST_CONTACT.ENABLED')
        : t('CONTACT_PANEL.TEST_CONTACT.DISABLED')
    );
  } catch (error) {
    if (error.contactErrorDetail) recusa.value = error.contactErrorDetail;
    else useAlert(t('CONTACT_PANEL.TEST_CONTACT.ERROR'));
  } finally {
    gravando.value = false;
  }
};
</script>

<!-- Some inteiro para quem não é administrador (ou não tem o `contacts:manage`), como o Policy. -->
<!-- eslint-disable vue/no-root-v-if -->
<template>
  <div v-if="marcaTeste" class="flex flex-col w-full gap-1">
    <div class="flex items-center justify-between w-full gap-3">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACT_PANEL.TEST_CONTACT.TITLE') }}
      </span>
      <Switch
        :model-value="contact.teste"
        :disabled="gravando || !!motivo"
        class="disabled:opacity-50 disabled:cursor-not-allowed"
        @update:model-value="alternar"
      />
    </div>
    <span
      class="text-xs"
      :class="motivo ? 'text-n-amber-11' : 'text-n-slate-11'"
    >
      {{ motivo || t('CONTACT_PANEL.TEST_CONTACT.DESCRIPTION') }}
    </span>
  </div>
</template>
